// ═══════════════════════════════════════════════════════════════════════════════
// WHIPT TRANSCRIPTS PANEL - Main Component
// ═══════════════════════════════════════════════════════════════════════════════
// NAVIGATION GUIDE:
//   1. [IMPORTS] - Dependencies and types
//   2. [STATE] - React state declarations  
//   3. [DATABASE] - Load/save/delete operations
//   4. [AUDIO] - Audio processing utilities
//   5. [HANDLERS] - User interaction handlers
//   6. [RENDER] - Component render logic
//
// Use Cmd+Shift+O (Mac) or Ctrl+Shift+O (Windows) to jump to functions
// Use Cmd+P (Mac) or Ctrl+P (Windows) then type @ to see outline
// ═══════════════════════════════════════════════════════════════════════════════

//#region IMPORTS
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { X, Upload, Loader2, Sparkles, Send, Copy, Download, Edit2, Save, FileText, Trash2, Pencil, Minimize2, Maximize2, Search, Menu, Play, Pause } from 'lucide-react';
import { supabase, supabaseAnonKey } from '@/lib/supabase';
import { createFlexibleRepository } from '@/repositories/flexibleEntityRepository';
import { useAuth } from '@/contexts/AuthContext';
import { extractDocumentText } from '@/lib/documentExtractor';
import { generateSummary } from '@/lib/summaryGenerator';
import { extractTextFromImage, isImageFile } from '@/lib/ocrService';
import { saveRecording, listSavedRecordings, loadRecording, deleteRecording, findRecordingByTranscriptId, updateRecordingTranscriptId } from '@/lib/recordingStorage';
import type { SavedRecording } from '@/lib/recordingStorage';
import { TranscriptSidebar } from './WhiptTranscriptsPanel/TranscriptSidebar';
import { TranscriptQA } from './WhiptTranscriptsPanel/TranscriptQA';
import { buttonStyles, containerStyles, layoutStyles } from './WhiptTranscriptsPanel/buttonStyles';
import { useWhiptNotification } from './WhiptNotification';
//#endregion

//#region TYPES
interface WhiptTranscriptsPanelProps {
  onClose?: () => void;
  initialResult?: any;
  onSaved?: () => void;
  embedded?: boolean; // When true, don't render own overlay (parent handles it)
  onMinimize?: (minimized: boolean) => void; // Notify parent of minimize state
}

interface QAPair {
  question: string;
  answer: string;
}

interface SavedTranscript {
  id: number | string;
  title: string;
  transcript: string;
  segments?: Array<{start: number, end: number, text: string}>;
  audioFileName?: string;
  createdAt: string;
  qaPairs?: QAPair[];
  section?: string;
}
//#endregion

// ═══════════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════════════════════

const WhiptTranscriptsPanel: React.FC<WhiptTranscriptsPanelProps> = ({ onClose, embedded = false, onMinimize }) => {
  const { user } = useAuth();
  const transcriptRepo = useMemo(() => createFlexibleRepository({ entityType: 'Transcript', orderBy: 'updated_at DESC' }), []);
  const { showNotification, NotificationComponent } = useWhiptNotification();
  
  const [isMobileDevice] = useState(() => {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  });
  
  //#region STATE DECLARATIONS
  const [audioFile, setAudioFile] = useState<File | null>(null);
  const [imageFiles, setImageFiles] = useState<File[]>([]); // Batch image uploads
  const [contextText, setContextText] = useState('');
  const [transcript, setTranscript] = useState('');
  const [transcriptSegments, setTranscriptSegments] = useState<Array<{start: number, end: number, text: string}>>([]);
  const [showTimestamps, setShowTimestamps] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingStep, setProcessingStep] = useState('');
  const [isMinimized, setIsMinimized] = useState(false);
  
  // Notify parent when minimize state changes
  useEffect(() => {
    if (onMinimize) {
      onMinimize(isMinimized);
    }
  }, [isMinimized, onMinimize]);
  const [isDragging, setIsDragging] = useState(false);
  
  // Q&A State
  const [showQA, setShowQA] = useState(false);
  const [qaPairs, setQAPairs] = useState<QAPair[]>([]);
  const [currentQuestion, setCurrentQuestion] = useState('');
  const [questionTemplate, setQuestionTemplate] = useState('');
  const [showTemplateEdit, setShowTemplateEdit] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [editedQuestion, setEditedQuestion] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  // Database State
  const [savedTranscripts, setSavedTranscripts] = useState<SavedTranscript[]>([]);
  const [currentTranscriptId, setCurrentTranscriptId] = useState<number | string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [editingTranscriptId, setEditingTranscriptId] = useState<number | string | null>(null);
  const [editedTitle, setEditedTitle] = useState('');
  const [showSidebar, setShowSidebar] = useState(false); // Mobile sidebar toggle

  // Tab Audio Recording State
  const [isRecording, setIsRecording] = useState(false);
  const [recordingDuration, setRecordingDuration] = useState(0);
  const [mediaRecorder, setMediaRecorder] = useState<MediaRecorder | null>(null);
  const [recordedChunks, setRecordedChunks] = useState<Blob[]>([]);
  const [pendingRecordings, setPendingRecordings] = useState<SavedRecording[]>([]);
  const [isPlaying, setIsPlaying] = useState(false);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const recordingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const keepAliveIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const silenceTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastSoundTimeRef = useRef<number>(Date.now());
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const currentRecIdRef = useRef<string | null>(null); // IndexedDB recording ID for cleanup

  // Document Upload & Summary State
  const [documentExtracted, setDocumentExtracted] = useState<{
    text: string;
    type: 'pdf' | 'docx' | 'txt';
    fileName: string;
    pageCount?: number;
    wordCount: number;
  } | null>(null);
  const [summary, setSummary] = useState<string>('');
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [showSummary, setShowSummary] = useState(false);

  const functionAuthHeaders = {
    Authorization: `Bearer ${supabaseAnonKey}`,
    apikey: supabaseAnonKey,
  };

  // Job persistence state for resumable transcription
  const [currentJobId, setCurrentJobId] = useState<string | null>(null);
  const [failedJobId, setFailedJobId] = useState<string | null>(
    () => localStorage.getItem('whipt_failed_job_id')
  );
  const [failedJobProgress, setFailedJobProgress] = useState<{chunkDone: number; chunkTotal: number} | null>(
    () => {
      const id = localStorage.getItem('whipt_failed_job_id');
      if (id) {
        return {
          chunkDone: parseInt(localStorage.getItem('whipt_failed_job_chunk_done') || '0', 10),
          chunkTotal: parseInt(localStorage.getItem('whipt_failed_job_chunk_total') || '0', 10),
        };
      }
      return null;
    }
  );
  //#endregion

  // ── Load pending recordings from IndexedDB on mount ──
  useEffect(() => {
    listSavedRecordings().then(setPendingRecordings).catch(console.error);
  }, []);

  //#region DATABASE OPERATIONS
  // Load saved transcripts on mount and when user changes
  useEffect(() => {
    if (user?.id) {
      loadSavedTranscripts();
    }
  }, [user?.id]);

  const loadSavedTranscripts = async () => {
    try {
      const items = await transcriptRepo.list(user?.id);
      
      const transcripts = items.map((item: any) => {
        // The repository returns items with data already spread into the object
        // But sometimes the data might still be nested
        const itemData = item.data || item;
        
        // Try multiple ways to get the title
        const title = item.title || 
                     itemData.title || 
                     item.audioFileName || 
                     itemData.audioFileName || 
                     'Untitled';
        
        return {
          id: item.id,
          title: title,
          transcript: item.transcript || itemData.transcript || '',
          segments: item.segments || itemData.segments || [],
          audioFileName: item.audioFileName || itemData.audioFileName,
          createdAt: item.created_at,
          qaPairs: item.qaPairs || itemData.qaPairs || [],
          section: item.section || itemData.section,
        };
      });
      
      setSavedTranscripts(transcripts);
    } catch (err) {
      console.error('Failed to load transcripts:', err);
    }
  };

  const saveCurrentSession = async (
    transcriptOverride?: string, 
    segmentsOverride?: Array<{start: number, end: number, text: string}>, 
    titleOverride?: string,
    modeOverride?: string,
    documentTypeOverride?: string,
    qaPairsOverride?: QAPair[],
    audioDatabaseIdOverride?: string | null
  ) => {
    const transcriptToSave = transcriptOverride || transcript;
    const segmentsToSave = segmentsOverride !== undefined ? segmentsOverride : transcriptSegments;
    const qaPairsToSave = qaPairsOverride !== undefined ? qaPairsOverride : qaPairs;
    const audioDbId = audioDatabaseIdOverride !== undefined ? audioDatabaseIdOverride : currentRecIdRef.current;
    
    if (!transcriptToSave || typeof transcriptToSave !== 'string' || transcriptToSave.trim().length === 0) {
      console.error('Cannot save - no transcript content');
      alert('Cannot save - no transcript content loaded. Please transcribe audio first.');
      return;
    }
    
    setIsSaving(true);
    try {
      // CRITICAL: For existing transcripts, ALWAYS preserve the existing title unless explicitly overridden
      let title: string;
      
      if (currentTranscriptId) {
        const existing = savedTranscripts.find(t => t.id === currentTranscriptId);
        if (existing?.title && existing.title !== '...' && existing.title.trim() !== '') {
          // PRESERVE existing title for updates
          title = existing.title;
          console.log('✅ Preserving existing title:', title);
        } else {
          // Existing transcript has bad title, need to fix it
          console.warn('⚠️ Existing transcript has bad title, generating new one');
          title = ''; // Will be set below
        }
      }
      
      // Only generate new title if we don't have one yet
      if (!title) {
        // Build title with proper fallbacks - NEVER allow just "..."
        if (titleOverride && titleOverride.trim() && titleOverride !== '...') {
          title = titleOverride;
        } else if (audioFile?.name) {
          title = audioFile.name;
        } else if (documentExtracted?.fileName) {
          title = documentExtracted.fileName;
        } else if (contextText && contextText.trim().length > 0) {
          title = contextText.substring(0, 50) + (contextText.length > 50 ? '...' : '');
        } else if (transcriptToSave && transcriptToSave.length > 10) {
          // Use first 50 chars of transcript as title
          title = transcriptToSave.substring(0, 50).trim() + '...';
        } else {
          // If we still have no title, generate a meaningful one with timestamp
          title = `Transcript ${new Date().toLocaleDateString()} ${new Date().toLocaleTimeString()}`;
        }
      }
      
      // CRITICAL VALIDATION: Never save "..." as a title
      if (title === '...' || title.trim() === '' || title.trim() === '...') {
        const userTitle = prompt('⚠️ ERROR: No title detected!\n\nPlease enter a title for this transcript:');
        if (!userTitle || userTitle.trim() === '') {
          alert('Cannot save without a title. Save cancelled.');
          setIsSaving(false);
          return;
        }
        title = userTitle.trim();
        console.error('🚨 BUG DETECTED: Title was "..." - user provided:', title);
      }
      
      const mode = modeOverride || (documentExtracted ? 'document' : audioFile ? 'audio' : 'text');
      
      // Get existing section if updating
      let existingSection = undefined;
      if (currentTranscriptId) {
        const existingTranscript = savedTranscripts.find(t => t.id === currentTranscriptId);
        existingSection = existingTranscript?.section;
      }
      
      const data = {
        title: title,
        transcript: transcriptToSave,
        segments: segmentsToSave,
        audioFileName: audioFile?.name || documentExtracted?.fileName,
        audioDatabaseId: audioDbId, // Link to IndexedDB recording
        mode: mode,
        documentType: documentTypeOverride || documentExtracted?.type,
        qaPairs: qaPairsToSave,
        summary: summary || undefined,
        section: existingSection,
        userId: user?.id,
      };
      
      // Validate title before saving
      if (!data.title || data.title.trim() === '' || data.title === '...') {
        console.error('Invalid title detected:', data.title);
        alert('SAVE BLOCKED: Invalid title. This is a bug - please report it.');
        setIsSaving(false);
        return;
      }

      if (currentTranscriptId) {
        await transcriptRepo.update(currentTranscriptId, data);
        // Also update the recording's transcriptId link
        if (audioDbId) {
          await updateRecordingTranscriptId(audioDbId, currentTranscriptId);
        }
        setShowSuccessPopup(true); // Show "saved for playback" popup
      } else {
        const newItem = await transcriptRepo.create(data);
        setCurrentTranscriptId(Number(newItem.id));
        // Link recording to the newly created transcript
        if (audioDbId) {
          await updateRecordingTranscriptId(audioDbId, Number(newItem.id));
        }
        setShowSuccessPopup(true);
      }
      
      await new Promise(resolve => setTimeout(resolve, 300));
      await loadSavedTranscripts();
      showNotification('Session saved successfully!', 'success');
    } catch (err: any) {
      console.error('Save failed:', err);
      showNotification(`Failed to save: ${err.message}`, 'error');
    } finally {
      setIsSaving(false);
    }
  };
  //#endregion

  //#region UTILITY FUNCTIONS
  // Format seconds to MM:SS
  const formatTimestamp = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Generate transcript text with optional timestamps
  const getFormattedTranscript = (): string => {
    if (!showTimestamps || transcriptSegments.length === 0) {
      return transcript;
    }
    
    return transcriptSegments
      .map(seg => `[${formatTimestamp(seg.start)}] ${seg.text}`)
      .join('\n');
  };

  const loadTranscript = (saved: SavedTranscript) => {
    if (!saved.transcript || saved.transcript.length === 0) {
      alert('This transcript has no content. Please delete it and create a new one.');
      return;
    }
    
    // Clear ALL previous state FIRST to prevent cross-contamination
    setQAPairs([]);
    setCurrentQuestion('');
    setQuestionTemplate('');
    setEditingIndex(null);
    setEditedQuestion('');
    setSearchQuery('');
    setShowQA(false);
    
    // Now load the new transcript
    const segments = saved.segments || [];
    setTranscript(saved.transcript);
    setTranscriptSegments(segments);
    setQAPairs(saved.qaPairs || []);
    setSummary((saved as any).summary || '');
    setShowSummary(!!((saved as any).summary));
    setCurrentTranscriptId(saved.id);
    setAudioFile(saved.audioFileName ? new File([], saved.audioFileName) : null);
    setContextText('');
    
    if (segments.length > 0) {
      setShowTimestamps(true);
    } else {
      setShowTimestamps(false);
    }
    
    if (saved.qaPairs && saved.qaPairs.length > 0) {
      setShowQA(true);
    } else {
      setShowQA(false);
    }

    // ── Auto-load linked audio for playback ──
    loadAudioForTranscript(saved);
  };

  // ── Audio Playback ──
  const loadAudioForTranscript = async (saved: SavedTranscript) => {
    const recDbId = (saved as any).audioDatabaseId;
    if (!recDbId) {
      // Try finding by transcript ID
      const found = await findRecordingByTranscriptId(saved.id);
      if (found) {
        createAudioUrl(found);
      }
      return;
    }
    const found = await loadRecording(recDbId);
    if (found) {
      createAudioUrl(found);
    }
  };

  const createAudioUrl = (recording: SavedRecording) => {
    // Clean up previous URL
    if (audioUrl) URL.revokeObjectURL(audioUrl);
    const url = URL.createObjectURL(recording.blob);
    setAudioUrl(url);
    if (audioRef.current) {
      audioRef.current.src = url;
    }
  };

  const togglePlayback = () => {
    if (!audioRef.current || !audioUrl) return;
    if (isPlaying) {
      audioRef.current.pause();
      setIsPlaying(false);
    } else {
      audioRef.current.play().catch(e => console.warn('Playback failed:', e));
      setIsPlaying(true);
    }
  };

  // Cleanup audio URL on unmount
  useEffect(() => {
    return () => {
      if (audioUrl) URL.revokeObjectURL(audioUrl);
    };
  }, []);
  //#endregion

  //#region AUDIO PROCESSING
  // Audio processing helpers (from old working version)
  const transcodeToWavBrowser = async (file: File) => {
    setProcessingStep('Preparing audio...');
    const arrayBuffer = await file.arrayBuffer();
    const audioCtx = new AudioContext();
    const decoded = await audioCtx.decodeAudioData(arrayBuffer.slice(0));
    const targetRate = 16000;
    const lengthSeconds = decoded.duration;
    const targetLength = Math.ceil(lengthSeconds * targetRate);
    const offline = new OfflineAudioContext(1, targetLength, targetRate);
    const source = offline.createBufferSource();
    const monoBuffer = offline.createBuffer(1, decoded.length, decoded.sampleRate);
    const tmp = new Float32Array(decoded.length);
    decoded.getChannelData(0).forEach((v, i) => { tmp[i] += v; });
    if (decoded.numberOfChannels > 1) {
      const ch1 = decoded.getChannelData(1);
      for (let i = 0; i < ch1.length; i++) tmp[i] += ch1[i];
      for (let i = 0; i < tmp.length; i++) tmp[i] = tmp[i] / decoded.numberOfChannels;
    }
    monoBuffer.copyToChannel(tmp, 0);
    source.buffer = monoBuffer;
    source.connect(offline.destination);
    source.start();
    const rendered = await offline.startRendering();
    const floatData = rendered.getChannelData(0);
    const wavBuffer = floatToWav(floatData, targetRate);
    return new File([wavBuffer], file.name.replace(/\.[^.]+$/, '') + '.wav', { type: 'audio/wav' });
  };

  const floatToWav = (float32: Float32Array, sampleRate: number) => {
    const bytesPerSample = 2;
    const buffer = new ArrayBuffer(44 + float32.length * bytesPerSample);
    const view = new DataView(buffer);
    const writeString = (o: number, s: string) => { for (let i = 0; i < s.length; i++) view.setUint8(o + i, s.charCodeAt(i)); };
    writeString(0, 'RIFF');
    view.setUint32(4, 36 + float32.length * bytesPerSample, true);
    writeString(8, 'WAVE');
    writeString(12, 'fmt ');
    view.setUint32(16, 16, true);
    view.setUint16(20, 1, true);
    view.setUint16(22, 1, true);
    view.setUint32(24, sampleRate, true);
    view.setUint32(28, sampleRate * 2, true);
    view.setUint16(32, 2, true);
    view.setUint16(34, 16, true);
    writeString(36, 'data');
    view.setUint32(40, float32.length * bytesPerSample, true);
    let offset = 44;
    for (let i = 0; i < float32.length; i++, offset += 2) {
      let s = Math.max(-1, Math.min(1, float32[i]));
      view.setInt16(offset, s < 0 ? s * 0x8000 : s * 0x7fff, true);
    }
    return buffer;
  };

  const segmentWav = async (file: File, minutes = 2.5) => {
    const bytes = new Uint8Array(await file.arrayBuffer());
    const view = new DataView(bytes.buffer);
    const sampleRate = view.getUint32(24, true);
    const bytesPerSample = 2;
    const chunkBytes = Math.floor(minutes * 60 * sampleRate * bytesPerSample);
    const dataOffset = 44;
    const dataLength = bytes.length - dataOffset;
    const totalChunks = Math.ceil(dataLength / chunkBytes);
    const segments: File[] = [];
    for (let i = 0; i < totalChunks; i++) {
      const start = dataOffset + i * chunkBytes;
      const end = Math.min(start + chunkBytes, bytes.length);
      const pcmSlice = bytes.subarray(start, end);
      const header = createWavHeader(pcmSlice.length / bytesPerSample, sampleRate, 1, 16);
      const wavBytes = new Uint8Array(header.byteLength + pcmSlice.length);
      wavBytes.set(new Uint8Array(header), 0);
      wavBytes.set(pcmSlice, header.byteLength);
      segments.push(new File([wavBytes.buffer], `${file.name.replace(/\.[^.]+$/, '')}-part-${i + 1}.wav`, { type: 'audio/wav' }));
    }
    return segments;
  };

  const createWavHeader = (numSamples: number, sampleRate: number, numChannels: number, bitsPerSample: number) => {
    const bytesPerSample = bitsPerSample / 8;
    const buffer = new ArrayBuffer(44);
    const view = new DataView(buffer);
    const writeString = (o: number, s: string) => { for (let i = 0; i < s.length; i++) view.setUint8(o + i, s.charCodeAt(i)); };
    writeString(0, 'RIFF');
    view.setUint32(4, 36 + numSamples * numChannels * bytesPerSample, true);
    writeString(8, 'WAVE');
    writeString(12, 'fmt ');
    view.setUint32(16, 16, true);
    view.setUint16(20, 1, true);
    view.setUint16(22, numChannels, true);
    view.setUint32(24, sampleRate, true);
    view.setUint32(28, sampleRate * numChannels * bytesPerSample, true);
    view.setUint16(32, numChannels * bytesPerSample, true);
    view.setUint16(34, bitsPerSample, true);
    writeString(36, 'data');
    view.setUint32(40, numSamples * numChannels * bytesPerSample, true);
    return buffer;
  };

  const bytesToDataUrl = (bytes: Uint8Array, mimeType: string) => {
    let binary = '';
    const chunkSize = 0x8000;
    for (let i = 0; i < bytes.length; i += chunkSize) {
      binary += String.fromCharCode(...bytes.subarray(i, i + chunkSize));
    }
    return `data:${mimeType};base64,${btoa(binary)}`;
  };

  const getAudioDuration = (bytes: Uint8Array): number => {
    // Read WAV file duration from header
    const view = new DataView(bytes.buffer);
    const sampleRate = view.getUint32(24, true);
    const bytesPerSample = 2; // 16-bit audio
    const dataOffset = 44; // WAV header size
    const dataLength = bytes.length - dataOffset;
    const numSamples = dataLength / bytesPerSample;
    const duration = numSamples / sampleRate; // Duration in seconds
    
    console.log(`  🔍 WAV Analysis: ${numSamples.toFixed(0)} samples at ${sampleRate}Hz = ${duration.toFixed(2)}s`);
    return duration;
  };

  const transcribeSegments = async (segments: File[], jobId?: string | null) => {
    let merged = '';
    let allSegments: Array<{start: number, end: number, text: string}> = [];
    let timeOffset = 0;
    
    console.log(`🎬 Starting transcription of ${segments.length} audio segments...`);
    
    // Resume support: check job state for already-completed chunks
    let resumeFromChunk = 0;
    if (jobId) {
      try {
        const accessToken = localStorage.getItem('whipt_session_token') || '';
        const statusResp = await fetch('/api/transcription_update', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${accessToken}` },
          body: JSON.stringify({ jobId }),
        });
        if (statusResp.ok) {
          const { job } = await statusResp.json();
          resumeFromChunk = job.chunk_done || 0;
          console.log(`⏭️ Resuming from chunk ${resumeFromChunk}/${segments.length} (job: ${jobId})`);
        }
      } catch (err) {
        console.warn('Could not read job state, starting fresh', err);
      }
    }
    
    for (let i = resumeFromChunk; i < segments.length; i++) {
      const bytes = new Uint8Array(await segments[i].arrayBuffer());
      
      // Calculate actual chunk duration from WAV file
      const chunkDuration = getAudioDuration(bytes);
      console.log(`📊 Chunk ${i + 1}: Duration = ${chunkDuration.toFixed(2)}s, Offset = ${timeOffset.toFixed(2)}s`);
      
      setProcessingStep(`Transcribing chunk ${i + 1}/${segments.length}...`);
      
      // Call Cloudflare Pages Function for transcription (secure, server-side API key)
      const formData = new FormData();
      const audioBlob = new Blob([bytes], { type: segments[i].type || 'audio/webm' });
      formData.append('file', audioBlob, `chunk_${i + 1}.webm`);
      
      const resp = await fetch('/api/transcribe', {
        method: 'POST',
        body: formData,
      });
      
      if (!resp.ok) {
        const errorText = await resp.text();
        let errorMessage = 'Transcription failed';
        try {
          const errorJson = JSON.parse(errorText);
          errorMessage = errorJson.error || errorMessage;
        } catch { /* text fallback */ }
        throw new Error(errorMessage);
      }
      
      const data = await resp.json();
      const transcriptText = data.text || '';
      
      merged += (merged ? '\n\n' : '') + transcriptText;
      
      console.log(`🎬 Chunk ${i + 1} response:`, { 
        hasSegments: !!data.segments, 
        segmentsLength: data.segments?.length || 0 
      });
      
      // Collect segments with adjusted timestamps for chunk offset
      if (data.segments && Array.isArray(data.segments) && data.segments.length > 0) {
        console.log(`📊 Chunk ${i + 1}: Received ${data.segments.length} segments`);
        
        // Log first segment of this chunk before adjustment
        if (data.segments.length > 0) {
          const firstSeg = data.segments[0];
          console.log(`  📌 First segment (before offset): [${firstSeg.start.toFixed(2)}s - ${firstSeg.end.toFixed(2)}s] "${firstSeg.text.substring(0, 30)}"`);
        }
        
        const adjustedSegments = data.segments.map((seg: any) => ({
          start: seg.start + timeOffset,
          end: seg.end + timeOffset,
          text: seg.text
        }));
        
        // Log first segment after adjustment
        if (adjustedSegments.length > 0) {
          const firstAdj = adjustedSegments[0];
          console.log(`  ✅ First segment (after offset +${timeOffset.toFixed(2)}s): [${firstAdj.start.toFixed(2)}s - ${firstAdj.end.toFixed(2)}s]`);
        }
        
        allSegments = allSegments.concat(adjustedSegments);
        
        // Use Whisper's last segment timestamp (not chunk file duration) to calculate next offset
        const lastSegment = data.segments[data.segments.length - 1];
        const actualTranscribedDuration = lastSegment.end;
        timeOffset += actualTranscribedDuration;
        console.log(`  ⏭️ Next chunk will start at offset: ${timeOffset.toFixed(2)}s (based on Whisper's last segment)`);
      } else {
        // Fallback: Create a simple segment for this chunk if no detailed segments
        console.warn(`⚠️ Chunk ${i + 1}: No segments in response, creating basic segment`);
        if (transcriptText.trim()) {
          allSegments.push({
            start: timeOffset,
            end: timeOffset + chunkDuration,
            text: transcriptText
          });
        }
        timeOffset += chunkDuration;
      }
      
      // ── Persist progress to transcription job after each chunk ──
      if (jobId) {
        try {
          const accessToken = localStorage.getItem('whipt_session_token') || '';
          await fetch('/api/transcription_update', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${accessToken}` },
            body: JSON.stringify({
              jobId,
              chunkDone: i + 1,
              chunkTotal: segments.length,
              transcriptText: merged,
              status: i + 1 < segments.length ? 'processing' : 'completed',
            }),
          });
          console.log(`💾 Job progress saved: chunk ${i + 1}/${segments.length}`);
        } catch (err) {
          console.warn('Failed to persist job progress for chunk', i + 1, err);
        }
      }
    }
    
    console.log(`✅ Transcription complete: ${merged.length} chars, ${allSegments.length} total segments`);
    console.log(`📊 Total duration: ${timeOffset.toFixed(2)}s (${(timeOffset / 60).toFixed(2)} minutes)`);
    return { transcript: merged, segments: allSegments };
  };

  const deleteTranscript = async (id: number) => {
    try {
      await transcriptRepo.remove(id);
      await loadSavedTranscripts();
      if (currentTranscriptId === id) {
        setTranscript('');
        setQAPairs([]);
        setCurrentTranscriptId(null);
        setShowQA(false);
      }
    } catch (err) {
      console.error('Delete failed:', err);
      alert('Failed to delete transcript.');
    }
  };

  const deleteAllEmptyTranscripts = async () => {
    console.log('🧹 Checking for empty transcripts...');
    
    try {
      // Fetch fresh data from database
      const items = await transcriptRepo.list();
      const allTranscripts = items.map((item: any) => ({
        id: item.id,
        title: item.title || item.audioFileName || 'Untitled',
        transcript: item.transcript || '',
        audioFileName: item.audioFileName,
        createdAt: item.created_at,
        qaPairs: item.qaPairs || [],
      }));
      
      // Check which are empty
      const emptyOnes = allTranscripts.filter(t => {
        const isEmpty = !t.transcript || t.transcript.length === 0;
        console.log(`  - "${t.title}" (ID: ${t.id}): ${isEmpty ? 'EMPTY' : `${t.transcript.length} chars`}`);
        return isEmpty;
      });
      
      if (emptyOnes.length === 0) {
        alert('No empty transcripts to delete');
        return;
      }
      
      if (!confirm(`Delete ${emptyOnes.length} empty transcript(s)?`)) return;
      
      console.log('🗑️ Deleting empty transcripts:', emptyOnes.map(t => t.title));
      for (const t of emptyOnes) {
        await transcriptRepo.remove(t.id);
      }
      await loadSavedTranscripts();
      alert(`✅ Deleted ${emptyOnes.length} empty transcript(s)`);
    } catch (err) {
      console.error('Bulk delete failed:', err);
      alert('Failed to delete empty transcripts.');
    }
  };

  const renameTranscript = async (id: number | string, newTitle: string) => {
    if (!newTitle.trim()) {
      setEditingTranscriptId(null);
      setEditedTitle('');
      return;
    }
    
    console.log('[Rename] Starting rename for ID:', id, 'New title:', newTitle);
    
    try {
      const items = await transcriptRepo.list();
      const item = items.find((i: any) => i.id === id);
      
      if (!item) {
        console.error('[Rename] Transcript not found:', id);
        return;
      }
      
      console.log('[Rename] Found item:', { id: item.id, oldTitle: item.title || item.data?.title });
      
      const data = {
        title: newTitle.trim(),
        transcript: item.transcript || item.data?.transcript || '',
        audioFileName: item.audioFileName || item.data?.audioFileName,
        qaPairs: item.qaPairs || item.data?.qaPairs || [],
        segments: item.segments || item.data?.segments || [],
        section: item.section || item.data?.section,
        summary: item.summary || item.data?.summary,
        userId: user?.id,
      };
      
      console.log('[Rename] Updating with data:', data);
      await transcriptRepo.update(id, data);
      console.log('[Rename] Update complete, reloading transcripts...');
      await loadSavedTranscripts();
      console.log('[Rename] Reload complete');
      
      setEditingTranscriptId(null);
      setEditedTitle('');
    } catch (err) {
      console.error('[Rename] Rename failed:', err);
      alert('Failed to rename transcript.');
    }
  };

  const updateTranscriptSection = async (id: number, sectionId: string | undefined) => {
    try {
      const items = await transcriptRepo.list();
      const item = items.find((i: any) => i.id === id);
      if (!item) {
        console.error('Transcript not found:', id);
        return;
      }

      const data = {
        title: item.title || 'Untitled',
        transcript: item.transcript || '',
        audioFileName: item.audioFileName,
        qaPairs: item.qaPairs || [],
        section: sectionId,
        userId: user?.id,
      };

      await transcriptRepo.update(id, data);
      await loadSavedTranscripts();
    } catch (err) {
      console.error('Update section failed:', err);
    }
  };

  // Process audio to transcript (with chunked processing)
  // Handle document upload and text extraction
  const handleProcessDocument = async (file: File) => {
    setIsProcessing(true);
    setProcessingStep('Extracting text from document...');
    
    try {
      const extracted = await extractDocumentText(file);
      setDocumentExtracted(extracted);
      setTranscript(extracted.text);
      setProcessingStep('');
      
      setTimeout(() => {
        saveCurrentSession(extracted.text, [], file.name, 'document', extracted.type);
      }, 500);
    } catch (err: any) {
      console.error('Document extraction failed:', err);
      alert(`Failed to extract text: ${err.message}`);
      setDocumentExtracted(null);
      setTranscript('');
    } finally {
      setIsProcessing(false);
      setProcessingStep('');
    }
  };

  // Handle image OCR processing
  const handleProcessImage = async (file: File) => {
    setIsProcessing(true);
    setProcessingStep('Extracting text from image...');
    
    try {
      const result = await extractTextFromImage(file, (progress) => {
        setProcessingStep(`Processing image: ${Math.round(progress * 100)}%`);
      });
      
      setTranscript(result.text);
      setProcessingStep('');
      
      console.log('✅ Image OCR completed:', {
        confidence: result.confidence,
        textLength: result.text.length,
      });
      
      // Auto-save OCR result
      setTimeout(() => {
        console.log('🔄 Auto-saving OCR transcript...');
        saveCurrentSession(result.text, [], file.name, 'image', 'ocr');
      }, 500);
    } catch (err: any) {
      console.error('Image OCR failed:', err);
      alert(`Failed to extract text from image: ${err.message}`);
      setTranscript('');
    } finally {
      setIsProcessing(false);
      setProcessingStep('');
    }
  };

  // Handle batch image processing
  const handleProcessBatchImages = async (files: File[]) => {
    if (files.length === 0) return;
    
    setIsProcessing(true);
    const allTexts: string[] = [];
    const pageSegments: Array<{start: number, end: number, text: string}> = [];
    const secondsPerPage = 15; // Simulate 15 seconds per page for navigation
    
    try {
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        setProcessingStep(`Processing image ${i + 1}/${files.length}: ${file.name}`);
        
        const result = await extractTextFromImage(file, (progress) => {
          setProcessingStep(`Image ${i + 1}/${files.length}: ${Math.round(progress * 100)}%`);
        });
        
        // Create timestamp for this page (simulated for navigation)
        const startTime = i * secondsPerPage;
        const endTime = (i + 1) * secondsPerPage;
        const timestamp = `[${Math.floor(startTime / 60)}:${String(startTime % 60).padStart(2, '0')}]`;
        
        // Detect citations in the text
        const citationPattern = /\(([A-Z][a-z]+(?:\s+(?:&|and)\s+[A-Z][a-z]+)?,?\s+\d{4}[a-z]?)\)|([A-Z][a-z]+(?:\s+(?:&|and)\s+[A-Z][a-z]+)?,?\s+\(\d{4}[a-z]?\))|(?:p{1,2}\.\s*\d+(?:-\d+)?)/g;
        const citations = result.text.match(citationPattern) || [];
        const citationNote = citations.length > 0 ? `\n📚 Citations found: ${citations.slice(0, 3).join(', ')}${citations.length > 3 ? ` +${citations.length - 3} more` : ''}` : '';
        
        const pageText = `\n\n━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n${timestamp} Page ${i + 1} | ${file.name}\n━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━${citationNote}\n\n${result.text}`;
        
        allTexts.push(pageText);
        
        // Add to segments array for timestamp navigation
        pageSegments.push({
          start: startTime,
          end: endTime,
          text: pageText
        });
        
        console.log(`✅ Image ${i + 1}/${files.length} processed:`, {
          fileName: file.name,
          confidence: result.confidence,
          textLength: result.text.length,
          citations: citations.length,
        });
      }
      
      const combinedText = allTexts.join('\n');
      setTranscript(combinedText);
      setTranscriptSegments(pageSegments);
      setShowTimestamps(true); // Enable timestamp view
      setProcessingStep('');
      
      // Auto-save batch result with segments
      setTimeout(() => {
        console.log('🔄 Auto-saving batch OCR transcript with page navigation...');
        const batchTitle = `${files.length} Pages - ${files[0].name.split('.')[0]}`;
        saveCurrentSession(combinedText, pageSegments, batchTitle, 'image', 'ocr-batch');
      }, 500);
      
      alert(`✅ Successfully processed ${files.length} images with page markers and citations!`);
    } catch (err: any) {
      console.error('Batch image OCR failed:', err);
      alert(`Failed to process images: ${err.message}`);
      setTranscript(allTexts.join('\n') || ''); // Save partial results
      setTranscriptSegments(pageSegments);
    } finally {
      setIsProcessing(false);
      setProcessingStep('');
      setImageFiles([]); // Clear batch
    }
  };

  // Generate AI summary
  const handleGenerateSummary = async () => {
    if (!transcript || summaryLoading) return;
    
    setSummaryLoading(true);
    try {
      const title = audioFile?.name || documentExtracted?.fileName || 'Transcript';
      const generatedSummary = await generateSummary(transcript, title);
      setSummary(generatedSummary);
      setShowSummary(true);
      
      // Update saved transcript with summary
      if (currentTranscriptId) {
        await transcriptRepo.update(currentTranscriptId, { summary: generatedSummary });
      }
    } catch (err: any) {
      console.error('Summary generation failed:', err);
      alert(`Failed to generate summary: ${err.message}`);
    } finally {
      setSummaryLoading(false);
    }
  };

  const handleProcessAudio = async (fileToProcess?: File) => {
    const fileToUse = fileToProcess || audioFile;
    if (!fileToUse) return;
    setIsProcessing(true);
    setProcessingStep('Starting...');
    
    let jobId: string | null = null;
    
    try {
      const processedAudio = await transcodeToWavBrowser(fileToUse);
      setProcessingStep('Segmenting audio...');
      let segments = await segmentWav(processedAudio, 2.5);
      if (!segments || segments.length === 0) segments = [processedAudio];
      
      // ── Create transcription job for resumable pipeline ──
      if (user?.id) {
        try {
          const accessToken = localStorage.getItem('whipt_session_token') || '';
          const jobResp = await fetch('/api/transcription_start', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${accessToken}` },
            body: JSON.stringify({
              userId: user.id,
              chunkTotal: segments.length,
              fileName: fileToUse.name,
            }),
          });
          if (jobResp.ok) {
            const jobData = await jobResp.json();
            jobId = jobData.jobId;
            setCurrentJobId(jobId);
            console.log('📋 Transcription job created:', jobId, 'chunks:', segments.length);
          }
        } catch (err) {
          console.warn('Could not create transcription job, continuing without persistence', err);
        }
      }
      
      const result = await transcribeSegments(segments, jobId);
      const transcriptText = result.transcript;
      const transcriptSegs = result.segments;
      
      if (!transcriptText || transcriptText.trim().length === 0) {
        throw new Error('No transcript generated');
      }
      
      setTranscript(transcriptText);
      setTranscriptSegments(transcriptSegs);
      setProcessingStep('');
      console.log('✅ Transcript loaded:', transcriptText.substring(0, 200) + '...');
      console.log('📊 Transcript length:', transcriptText.length, 'characters');
      console.log('📊 Segments count:', transcriptSegs.length);
      console.log('📊 Timestamps available:', transcriptSegs.length > 0 && transcriptSegs[0].start !== undefined);
      console.log('📊 Segments state will be set to:', transcriptSegs.length, 'items');
      console.log('🔍 First 3 segments:', transcriptSegs.slice(0, 3));
      
      // ── Clear any failed job state on success ──
      if (localStorage.getItem('whipt_failed_job_id')) {
        localStorage.removeItem('whipt_failed_job_id');
        localStorage.removeItem('whipt_failed_job_chunk_done');
        localStorage.removeItem('whipt_failed_job_chunk_total');
      }
      setCurrentJobId(null);
      setFailedJobId(null);
      setFailedJobProgress(null);
      
      // ── Keep recording in IndexedDB for playback ──
      if (currentRecIdRef.current) {
        // Link the recording to the transcript (once we have its ID)
        console.log('💾 Keeping recording for playback:', currentRecIdRef.current);
      }
      
      // Save immediately with explicit title for persistence
      const title = fileToUse.name.replace(/\.[^.]+$/, '') || `Recording ${new Date().toLocaleDateString()}`;
      setTimeout(() => {
        console.log('🔄 Auto-saving after audio processing...');
        console.log('🔍 Passing segments to save:', transcriptSegs.length);
        saveCurrentSession(transcriptText, transcriptSegs, title);
      }, 500);
    } catch (err: any) {
      console.error('Transcription failed:', err);
      
      // ── Persist failure to job ──
      if (jobId) {
        try {
          const accessToken = localStorage.getItem('whipt_session_token') || '';
          await fetch('/api/transcription_update', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${accessToken}` },
            body: JSON.stringify({
              jobId,
              status: 'failed',
              error: `Transcription failed: ${err.message}`,
            }),
          });
          localStorage.setItem('whipt_failed_job_id', jobId);
          setFailedJobId(jobId);
          setFailedJobProgress({ chunkDone: 0, chunkTotal: 0 }); // will be fetched on resume
        } catch (e) {
          console.warn('Could not persist job failure', e);
        }
      }
      
      alert(`Failed to process audio: ${err.message}`);
      setTranscript('');
      setTranscriptSegments([]);
    } finally {
      setIsProcessing(false);
      setProcessingStep('');
    }
  };

  // ── Resume a failed transcription job ──
  const resumeTranscription = async () => {
    const storedJobId = localStorage.getItem('whipt_failed_job_id');
    if (!storedJobId || !audioFile) {
      showNotification('No audio file available to resume. Please re-upload.', 'error');
      return;
    }
    
    setIsProcessing(true);
    setProcessingStep('Resuming transcription...');
    
    try {
      const processedAudio = await transcodeToWavBrowser(audioFile);
      setProcessingStep('Segmenting audio...');
      let segments = await segmentWav(processedAudio, 2.5);
      if (!segments || segments.length === 0) segments = [processedAudio];
      
      const result = await transcribeSegments(segments, storedJobId);
      const transcriptText = result.transcript;
      const transcriptSegs = result.segments;
      
      if (!transcriptText || transcriptText.trim().length === 0) {
        throw new Error('No transcript generated');
      }
      
      setTranscript(transcriptText);
      setTranscriptSegments(transcriptSegs);
      
      // Clear failed job state
      localStorage.removeItem('whipt_failed_job_id');
      localStorage.removeItem('whipt_failed_job_chunk_done');
      localStorage.removeItem('whipt_failed_job_chunk_total');
      setFailedJobId(null);
      setFailedJobProgress(null);
      setCurrentJobId(null);
      
      // Auto-save with explicit title
      const title = audioFile.name.replace(/\.[^.]+$/, '') || `Recording ${new Date().toLocaleDateString()}`;
      setTimeout(() => saveCurrentSession(transcriptText, transcriptSegs, title), 500);
    } catch (err: any) {
      console.error('Resume failed:', err);
      alert(`Failed to resume: ${err.message}`);
    } finally {
      setIsProcessing(false);
      setProcessingStep('');
    }
  };

  const clearFailedJob = () => {
    localStorage.removeItem('whipt_failed_job_id');
    localStorage.removeItem('whipt_failed_job_chunk_done');
    localStorage.removeItem('whipt_failed_job_chunk_total');
    setFailedJobId(null);
    setFailedJobProgress(null);
  };

  // Use pasted text as transcript
  const handleUseContextText = () => {
    if (!contextText.trim()) return;
    setTranscript(contextText);
  };

  // Clean up AI response - remove escape characters and fix formatting
  const cleanAIResponse = (text: string): string => {
    let cleaned = text;
    
    // Remove leading/trailing quotes
    cleaned = cleaned.replace(/^["']|["']$/g, '');
    
    // Remove escape characters
    cleaned = cleaned.replace(/\\"/g, '"');
    cleaned = cleaned.replace(/\\n/g, '\n');
    cleaned = cleaned.replace(/\\\\/g, '\\');
    
    // Remove trailing "}
    cleaned = cleaned.replace(/"\}$/g, '');
    
    // Clean up any remaining escape sequences
    cleaned = cleaned.trim();
    
    return cleaned;
  };

  // Filter Q&A pairs based on search query
  const filteredQAPairs = useMemo(() => {
    if (!searchQuery.trim()) return qaPairs;
    
    const query = searchQuery.toLowerCase();
    return qaPairs.filter((qa, idx) => 
      qa.question.toLowerCase().includes(query) ||
      qa.answer.toLowerCase().includes(query) ||
      `q${idx + 1}`.includes(query)
    );
  }, [qaPairs, searchQuery]);

  // Generate answer for current question
  const handleGetAnswer = async () => {
    if (!currentQuestion.trim() || isGenerating || !transcript) return;
    
    setIsGenerating(true);
    try {
      // Combine question with template (if template exists)
      // Add timestamp instruction only if timestamps are enabled
      const timestampInstruction = showTimestamps && transcriptSegments.length > 0
        ? '\n\nPlease include accurate timestamp references from the transcript in your answer.'
        : '';
      
      const fullQuestion = questionTemplate.trim() 
        ? `${currentQuestion.trim()}\n\n${questionTemplate.trim()}${timestampInstruction}`
        : `${currentQuestion.trim()}${timestampInstruction}`;
      
      // Call Q&A Cloudflare Function (secure proxy)
      const resp = await fetch('/api/answer-question', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          transcript: transcript,
          question: fullQuestion,
        }),
      });
      
      if (!resp.ok) {
        const error = await resp.json();
        throw new Error(error.error || `HTTP ${resp.status}`);
      }
      
      const data = await resp.json();
      const newAnswer = data.answer || 'No answer returned';
      const answerText = typeof newAnswer === 'string' ? newAnswer : JSON.stringify(newAnswer);
      const cleanedAnswer = cleanAIResponse(answerText);
      
      const updatedPairs = [...qaPairs, { 
        question: currentQuestion.trim(), // Store only the question (not template)
        answer: cleanedAnswer 
      }];
      setQAPairs(updatedPairs);
      setCurrentQuestion('');
      
      // Auto-save after each Q&A with updated pairs - preserve existing title
      const existingTitle = currentTranscriptId 
        ? savedTranscripts.find(t => t.id === currentTranscriptId)?.title 
        : undefined;
      setTimeout(() => saveCurrentSession(undefined, undefined, existingTitle, undefined, undefined, updatedPairs), 500);
    } catch (err: any) {
      console.error('Answer generation failed:', err);
      showNotification(`Failed to generate answer: ${err.message}`, 'error');
    } finally {
      setIsGenerating(false);
    }
  };

  // Regenerate answer for edited question
  const handleRegenerateAnswer = async (index: number) => {
    if (!editedQuestion.trim() || isGenerating || !transcript) return;
    
    setIsGenerating(true);
    try {
      // Add timestamp instruction only if timestamps are enabled
      const timestampInstruction = showTimestamps && transcriptSegments.length > 0
        ? '\n\nPlease include accurate timestamp references from the transcript in your answer.'
        : '';
      
      const fullQuestion = questionTemplate.trim() 
        ? `${editedQuestion.trim()}\n\n${questionTemplate.trim()}${timestampInstruction}`
        : `${editedQuestion.trim()}${timestampInstruction}`;
      
      // Call Q&A Cloudflare Function (secure proxy)
      const resp = await fetch('/api/answer-question', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          transcript: transcript,
          question: fullQuestion,
        }),
      });
      
      if (!resp.ok) {
        const error = await resp.json();
        throw new Error(error.error || `HTTP ${resp.status}`);
      }
      
      const data = await resp.json();
      const newAnswer = data.answer || 'No answer returned';
      const answerText = typeof newAnswer === 'string' ? newAnswer : JSON.stringify(newAnswer);
      const cleanedAnswer = cleanAIResponse(answerText);
      
      const updatedPairs = [...qaPairs];
      updatedPairs[index] = { question: editedQuestion.trim(), answer: cleanedAnswer };
      setQAPairs(updatedPairs);
      setEditingIndex(null);
      setEditedQuestion('');
      
      // Auto-save after regenerating - preserve existing title
      const existingTitle = currentTranscriptId 
        ? savedTranscripts.find(t => t.id === currentTranscriptId)?.title 
        : undefined;
      setTimeout(() => saveCurrentSession(undefined, undefined, existingTitle, undefined, undefined, updatedPairs), 500);
    } catch (err: any) {
      console.error('Answer regeneration failed:', err);
      showNotification(`Failed to regenerate: ${err.message}`, 'error');
    } finally {
      setIsGenerating(false);
    }
  }; 
  const handleCopyAll = () => {
    const allQA = qaPairs
      .map((qa, i) => `Q${i + 1}: ${qa.question}\nA${i + 1}: ${qa.answer}`)
      .join('\n\n');
    navigator.clipboard.writeText(allQA).then(() => {
      alert('✅ Copied all Q&A to clipboard!');
    });
  };

  const handleDownload = () => {
    const allQA = qaPairs
      .map((qa, i) => `Q${i + 1}: ${qa.question}\nA${i + 1}: ${qa.answer}`)
      .join('\n\n');
    const blob = new Blob([allQA], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `qa-session-${Date.now()}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Tab Audio Recording Functions
  const startTabAudioRecording = async () => {
    try {
      // Request screen/tab capture with audio
      const stream = await navigator.mediaDevices.getDisplayMedia({
        video: true, // Required for tab capture, but we only need audio
        audio: {
          echoCancellation: false,
          noiseSuppression: false,
          sampleRate: 44100,
        }
      });

      // Check if audio track exists
      const audioTracks = stream.getAudioTracks();
      if (audioTracks.length === 0) {
        alert('⚠️ No audio track detected. Make sure to check "Share tab audio" in the browser prompt!');
        stream.getTracks().forEach(track => track.stop());
        return;
      }

      // Create audio-only stream
      const audioStream = new MediaStream(audioTracks);
      
      // Stop video track (we don't need it)
      stream.getVideoTracks().forEach(track => track.stop());

      // Auto-stop when tab audio source ends (user stops sharing or playback completes)
      const audioTrack = audioStream.getAudioTracks()[0];
      if (audioTrack) {
        audioTrack.onended = () => {
          console.log('🔇 Tab audio source ended — auto-stopping recording');
          if (isRecording) {
            showNotification('Recording stopped — tab audio ended', 'info');
            stopTabAudioRecording();
          }
        };
      }

      // Set up silence detection
      const audioContext = new AudioContext();
      const source = audioContext.createMediaStreamSource(audioStream);
      const analyser = audioContext.createAnalyser();
      analyser.fftSize = 2048;
      source.connect(analyser);
      
      audioContextRef.current = audioContext;
      analyserRef.current = analyser;
      lastSoundTimeRef.current = Date.now();
      
      // Monitor audio levels for silence detection
      const dataArray = new Uint8Array(analyser.frequencyBinCount);
      const checkSilence = () => {
        if (!analyserRef.current || !isRecording) return;
        
        analyser.getByteFrequencyData(dataArray);
        const average = dataArray.reduce((a, b) => a + b) / dataArray.length;
        
        // If there's sound (threshold: 5), update last sound time
        if (average > 5) {
          lastSoundTimeRef.current = Date.now();
        } else {
          // Check if silence has lasted 10 minutes (600000ms)
          const silenceDuration = Date.now() - lastSoundTimeRef.current;
          if (silenceDuration > 600000) { // 10 minutes
            console.log('🔇 10 minutes of silence detected - auto-stopping recording');
            showNotification('Recording stopped after 10 minutes of silence', 'info');
            stopTabAudioRecording();
            return;
          }
        }
        
        // Check again in 1 second
        if (isRecording) {
          silenceTimeoutRef.current = setTimeout(checkSilence, 1000);
        }
      };
      
      // Start monitoring
      checkSilence();

      // Create MediaRecorder
      const recorder = new MediaRecorder(audioStream, {
        mimeType: 'audio/webm',
      });

      const chunks: Blob[] = [];

      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunks.push(event.data);
        }
      };

recorder.onstop = async () => {
        console.log('🛑 Recording stopped, processing audio...');
        const audioBlob = new Blob(chunks, { type: 'audio/webm' });
        const recFileName = `recording-${Date.now()}.webm`;
        const audioFile = new File([audioBlob], recFileName, { type: 'audio/webm' });
        console.log(`📦 Recorded ${chunks.length} chunks, total size: ${audioBlob.size} bytes`);

        // 💾 SAVE full raw blob to IndexedDB before processing
        const recId = await saveRecording(audioBlob, recFileName, recordingDuration);
        currentRecIdRef.current = recId;
        console.log(`💾 Full recording saved to IndexedDB: ${recId}`);
        // Refresh pending list
        listSavedRecordings().then(setPendingRecordings).catch(console.error);

        // Set as audio file and process
        setAudioFile(audioFile);
        setRecordedChunks([]);
        setRecordingDuration(0);

        // Auto-process the recording
        setIsProcessing(true);
        setProcessingStep('Converting recorded audio...');

        // Pass the file directly to avoid race condition with setState
        await handleProcessAudio(audioFile);
      };

      // Initialize recording state BEFORE starting recorder
      setRecordingDuration(0);
      setIsRecording(true);
      setRecordedChunks([]);
      // Start duration timer FIRST
      recordingIntervalRef.current = setInterval(() => {
        setRecordingDuration(prev => {
          const next = prev + 1;
          console.log(`⏱️ Recording: ${Math.floor(next/60)}:${String(next%60).padStart(2, '0')}`);
          return next;
        });
      }, 1000);
      
      // Start keep-alive to prevent session timeout during recording
      keepAliveIntervalRef.current = setInterval(async () => {
        try {
          const { updateUserActivity } = await import('@/lib/auth');
          const userStr = localStorage.getItem('whipt_user');
          if (userStr) {
            const user = JSON.parse(userStr);
            if (user?.id) {
              await updateUserActivity(user.id);
              console.log('🔄 Session keep-alive (recording)');
            }
          }
        } catch (err) {
          console.warn('Keep-alive failed:', err);
        }
      }, 10000); // Ping every 10 seconds to prevent auto-logout
      
      // Then start recorder
      recorder.start(1000); // Collect data every second
      setMediaRecorder(recorder);

      console.log('✅ Started tab audio recording');
    } catch (err) {
      console.error('❌ Failed to start recording:', err);
      alert('Failed to start recording. Make sure to select a tab and check "Share tab audio"!');
    }
  };

  const stopTabAudioRecording = () => {
    if (mediaRecorder && mediaRecorder.state !== 'inactive') {
      mediaRecorder.stop();
      mediaRecorder.stream.getTracks().forEach(track => track.stop());
      setMediaRecorder(null);
      setIsRecording(false);
      
      if (recordingIntervalRef.current) {
        clearInterval(recordingIntervalRef.current);
        recordingIntervalRef.current = null;
      }
      
      if (keepAliveIntervalRef.current) {
        clearInterval(keepAliveIntervalRef.current);
        keepAliveIntervalRef.current = null;
        console.log('🛑 Keep-alive stopped');
      }
      
      // Clean up silence detection
      if (silenceTimeoutRef.current) {
        clearTimeout(silenceTimeoutRef.current);
        silenceTimeoutRef.current = null;
      }
      
      if (audioContextRef.current) {
        audioContextRef.current.close();
        audioContextRef.current = null;
      }
      
      analyserRef.current = null;
      
      console.log('⏹️ Stopped tab audio recording');
    }
  };

  // Format duration as MM:SS
  const formatDuration = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Cleanup on unmount only (no dependencies to avoid clearing interval on state updates)
  useEffect(() => {
    return () => {
      if (recordingIntervalRef.current) {
        clearInterval(recordingIntervalRef.current);
        recordingIntervalRef.current = null;
      }
    };
  }, []); // Empty dependency array - only runs on mount/unmount

  return (
    <>
      {/* Branded notifications */}
      {NotificationComponent}
      
      {/* Minimized floating button */}
      {isMinimized && (
        <button
          onClick={() => setIsMinimized(false)}
          className={buttonStyles.minimized}
          title="Restore Transcription Panel"
        >
          <FileText className="w-5 h-5 text-white" />
          <span className="text-sm font-semibold text-white">Transcription</span>
          {(transcript || qaPairs.length > 0) && (
            <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></span>
          )}
        </button>
      )}

      {/* Full panel */}
      {!isMinimized && (() => {
        const panelContent = (
          <div className={layoutStyles.mainPanel}>
        
        {/* Responsive Sidebar - Mobile overlay + Desktop fixed */}
        {/* Mobile Sidebar (overlay) */}
        {showSidebar && (
          <TranscriptSidebar
            savedTranscripts={savedTranscripts}
            currentTranscriptId={currentTranscriptId}
            editingTranscriptId={editingTranscriptId}
            editedTitle={editedTitle}
            isMobile={true}
            showSidebar={showSidebar}
            userId={user?.id}
            onClose={() => setShowSidebar(false)}
            onLoadTranscript={loadTranscript}
            onSetEditingTranscriptId={setEditingTranscriptId}
            onSetEditedTitle={setEditedTitle}
            onRenameTranscript={renameTranscript}
            onDeleteTranscript={deleteTranscript}
            onDeleteAllEmpty={deleteAllEmptyTranscripts}
            onUpdateTranscriptSection={updateTranscriptSection}
          />
        )}
        
        {/* Desktop Sidebar (always visible on md+) */}
        <div className="hidden md:block">
          <TranscriptSidebar
            savedTranscripts={savedTranscripts}
            currentTranscriptId={currentTranscriptId}
            editingTranscriptId={editingTranscriptId}
            editedTitle={editedTitle}
            isMobile={false}
            userId={user?.id}
            onLoadTranscript={loadTranscript}
            onSetEditingTranscriptId={setEditingTranscriptId}
            onSetEditedTitle={setEditedTitle}
            onRenameTranscript={renameTranscript}
            onDeleteTranscript={deleteTranscript}
            onDeleteAllEmpty={deleteAllEmptyTranscripts}
            onUpdateTranscriptSection={updateTranscriptSection}
          />
        </div>

        {/* Main Content */}
        <div className="flex-1 flex flex-col min-w-0">
          {/* Header */}
          <div className="flex items-center justify-between p-3 md:p-6 border-b border-slate-700">
            <div className="flex-1 min-w-0 flex items-center gap-2">
              {/* Hamburger Menu - Mobile Only */}
              <button
                onClick={() => setShowSidebar(!showSidebar)}
                className="md:hidden p-2 hover:bg-slate-800 rounded-lg transition-colors flex-shrink-0"
                title="Saved Transcripts"
              >
                <Menu className="w-5 h-5 text-slate-400" />
              </button>
              
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 md:gap-3">
                  <Sparkles className="w-5 h-5 md:w-6 md:h-6 text-amber-400 flex-shrink-0" />
                  <h2 className="text-lg md:text-2xl font-bold text-white truncate">WHIPT Transcripts</h2>
                </div>
                {currentTranscriptId && (
                  <p className="text-[10px] md:text-xs text-slate-400 mt-1 ml-7 md:ml-9 truncate">
                    {savedTranscripts.find(t => t.id === currentTranscriptId)?.title || 'Current Session'}
                  </p>
                )}
              </div>
            </div>
            <div className="flex items-center gap-1 md:gap-2 flex-shrink-0">
              {transcript && (
                <>
                  <button
                    onClick={() => {
                      if (confirm('Start a new transcript? Unsaved changes will be lost.')) {
                        setTranscript('');
                        setTranscriptSegments([]);
                        setQAPairs([]);
                        setCurrentTranscriptId(null);
                        setShowQA(false);
                        setAudioFile(null);
                        setContextText('');
                      }
                    }}
                    className="px-2 md:px-3 py-1.5 rounded-lg border border-purple-400/60 bg-purple-500/10 hover:bg-purple-500/20 text-purple-100 text-xs flex items-center gap-1.5"
                  >
                    <Upload className="w-3 h-3 md:w-3.5 md:h-3.5" />
                    <span className="hidden sm:inline">New Transcript</span>
                  </button>
                  <button
                    onClick={() => saveCurrentSession()}
                    disabled={isSaving}
                    className="px-2 md:px-3 py-1.5 rounded-lg border border-emerald-400/60 bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-100 text-xs flex items-center gap-1.5 disabled:opacity-50"
                  >
                    <Save className="w-3 h-3 md:w-3.5 md:h-3.5" />
                    <span>{isSaving ? 'Saving...' : 'Save'}</span>
                  </button>
                </>
              )}
              <button
                onClick={() => setIsMinimized(true)}
                className="hidden md:block p-2 hover:bg-slate-800 rounded-lg transition-colors"
                title="Minimize"
              >
                <Minimize2 className="w-5 h-5 text-slate-400" />
              </button>
              {onClose && (
                <button onClick={onClose} className="p-2 hover:bg-slate-800 rounded-lg transition-colors">
                  <X className="w-4 h-4 md:w-5 md:h-5 text-slate-400" />
                </button>
              )}
            </div>
          </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4 md:p-6 space-y-4 md:space-y-6">
          {!transcript && !showQA ? (
            // Step 1: Upload or Paste
            <div className="space-y-4 md:space-y-6">
              {/* ═══════════════ Resume Banner ═══════════════ */}
              {failedJobId && failedJobProgress && (
                <div className="rounded-xl border-2 border-amber-500/30 bg-amber-950/30 backdrop-blur-xl p-4 space-y-3">
                  <div className="flex items-center gap-2">
                    <svg className="w-5 h-5 text-amber-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                    <p className="text-sm font-semibold text-amber-200">
                      Incomplete transcription detected ({failedJobProgress.chunkDone}/{failedJobProgress.chunkTotal} chunks completed)
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={resumeTranscription}
                      disabled={isProcessing}
                      className="flex-1 px-4 py-2 rounded-lg bg-amber-600/30 backdrop-blur-xl border-2 border-amber-400/60 text-white text-sm font-semibold shadow-lg disabled:opacity-50 hover:bg-amber-600/40 transition-all flex items-center justify-center gap-2"
                    >
                      {isProcessing ? (
                        <><Loader2 className="w-4 h-4 animate-spin" /> Resuming...</>
                      ) : (
                        <><svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" /></svg> Resume transcription</>
                      )}
                    </button>
                    <button
                      onClick={clearFailedJob}
                      disabled={isProcessing}
                      className="px-4 py-2 rounded-lg border border-slate-600 text-slate-300 text-sm hover:bg-slate-800 transition-colors backdrop-blur-xl"
                    >
                      Discard
                    </button>
                  </div>
                </div>
              )}
              {/* ═══════════════ Saved Recordings (Pending Retry) ═══════════════ */}
              {pendingRecordings.length > 0 && (
                <div className="rounded-xl border-2 border-emerald-500/30 bg-emerald-950/20 p-4 md:p-6 space-y-3">
                  <div className="flex items-center justify-between">
                    <h3 className="text-base md:text-lg font-semibold text-emerald-100 flex items-center gap-2">
                      <svg className="w-4 h-4 md:w-5 md:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
                      </svg>
                      Saved Recordings ({pendingRecordings.length})
                    </h3>
                  </div>
                  <p className="text-xs text-emerald-300/70">Full raw recordings saved — retry transcription when ready.</p>
                  <div className="max-h-48 overflow-y-auto space-y-2">
                    {pendingRecordings.map((rec) => (
                      <div key={rec.id} className="flex items-center justify-between p-3 bg-emerald-900/40 rounded-lg border border-emerald-500/30">
                        <div className="flex-1 min-w-0">
                          <p className="text-xs text-emerald-200 truncate font-medium">{rec.fileName}</p>
                          <p className="text-[10px] text-emerald-400/60">
                            {new Date(rec.createdAt).toLocaleString()} · {formatDuration(rec.duration)}
                          </p>
                        </div>
                        <div className="flex items-center gap-1 flex-shrink-0 ml-2">
                          <button
                            onClick={async () => {
                              const saved = await loadRecording(rec.id);
                              if (!saved) return;
                              setAudioFile(new File([saved.blob], saved.fileName, { type: saved.blob.type }));
                              setTimeout(() => handleProcessAudio(), 500);
                            }}
                            disabled={isProcessing}
                            className="px-3 py-1.5 rounded-lg bg-emerald-600/30 border border-emerald-400/60 text-emerald-100 text-xs font-semibold hover:bg-emerald-600/40 transition-all disabled:opacity-50"
                          >
                            {isProcessing ? 'Processing...' : 'Retry'}
                          </button>
                          <button
                            onClick={async () => {
                              await deleteRecording(rec.id);
                              setPendingRecordings(prev => prev.filter(r => r.id !== rec.id));
                            }}
                            className="p-1.5 rounded-lg hover:bg-red-900/40 text-emerald-400 hover:text-red-400 transition-colors"
                            title="Delete"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex flex-col gap-4 md:gap-6">
                {/* Audio Upload with Drag & Drop */}
                <div
                  className={`rounded-xl border-2 ${isDragging ? 'border-purple-400 bg-purple-900/40' : 'border-purple-500/30 bg-purple-950/20'} p-4 md:p-6 space-y-4 transition-all`}
                  onDragOver={(e) => {
                    e.preventDefault();
                    setIsDragging(true);
                  }}
                  onDragLeave={(e) => {
                    e.preventDefault();
                    setIsDragging(false);
                  }}
                  onDrop={(e) => {
                    e.preventDefault();
                    setIsDragging(false);
                    const file = e.dataTransfer.files?.[0];
                    if (file) {
                      setAudioFile(file);
                    }
                  }}
                >
                  <h3 className="text-base md:text-lg font-semibold text-purple-100 flex items-center gap-2">
                    <Upload className="w-4 h-4 md:w-5 md:h-5" />
                    Upload Audio/Video/Image/Document
                  </h3>
                  
                  {isDragging ? (
                    <div className="flex flex-col items-center justify-center py-6 md:py-8 text-center">
                      <Upload className="w-10 h-10 md:w-12 md:h-12 text-purple-400 mb-3 animate-bounce" />
                      <p className="text-base md:text-lg font-semibold text-purple-200">Drop your file here!</p>
                      <p className="text-xs md:text-sm text-purple-300 mt-1">Audio, video, images, PDF, Word, or text files</p>
                    </div>
                  ) : (
                    <>
                      <div className="flex flex-col items-center justify-center py-4 md:py-6 border-2 border-dashed border-purple-500/40 rounded-lg bg-purple-950/30">
                        <Upload className="w-8 h-8 md:w-10 md:h-10 text-purple-400 mb-2" />
                        <p className="text-xs md:text-sm text-purple-200 font-semibold mb-1">Drag & drop file here</p>
                        <p className="text-[10px] md:text-xs text-purple-300">Audio, video, images, PDF, Word, or text files</p>
                      </div>
                      <input
                        type="file"
                        accept="audio/*,video/*,image/*,.pdf,.docx,.doc,.txt"
                        onChange={(e) => setAudioFile(e.target.files?.[0] || null)}
                        className="w-full text-xs md:text-sm text-slate-300 file:mr-2 md:file:mr-4 file:py-2 file:px-3 md:file:px-4 file:rounded-lg file:border-0 file:bg-purple-600 file:text-white hover:file:bg-purple-700 file:cursor-pointer file:text-xs md:file:text-sm"
                      />
                      
                      {/* Batch Image Upload */}
                      <div className="mt-4 p-3 bg-purple-900/30 rounded-lg border border-purple-500/20">
                        <p className="text-xs text-purple-300 mb-2 flex items-center gap-2">
                          <Sparkles className="w-3 h-3" />
                          <span className="font-semibold">Batch Upload:</span> Select multiple images to combine into one transcript
                        </p>
                        <p className="text-[10px] text-amber-400 mb-2 italic">
                          💡 Hold Cmd (Mac) or Ctrl (Windows) while clicking to select multiple files
                        </p>
                        <label className="block">
                          <input
                            id="batch-image-upload"
                            type="file"
                            accept="image/*"
                            multiple
                            onChange={(e) => {
                              const files = Array.from(e.target.files || []);
                              console.log('🖼️ Selected files:', files.length, files.map(f => f.name));
                              setImageFiles(files);
                              setAudioFile(null); // Clear single file
                            }}
                            className="w-full text-xs text-slate-300 file:mr-2 file:py-1.5 file:px-3 file:rounded-lg file:border-0 file:bg-emerald-600 file:text-white hover:file:bg-emerald-700 file:cursor-pointer file:text-xs"
                          />
                        </label>
                      </div>
                    </>
                  )}
                  
                  {audioFile && (
                    <div className="flex items-center justify-between p-2 md:p-3 bg-purple-900/40 rounded-lg border border-purple-500/30">
                      <p className="text-xs md:text-sm text-purple-200 flex items-center gap-2 truncate flex-1 min-w-0">
                        <FileText className="w-3 h-3 md:w-4 md:h-4 flex-shrink-0" />
                        <span className="truncate">{audioFile.name}</span>
                      </p>
                      <div className="flex items-center gap-1 flex-shrink-0">
                        {/* Download button to save audio file before refresh */}
                        <button
                          onClick={() => {
                            // Create a download link for the raw audio blob
                            const url = URL.createObjectURL(audioFile);
                            const a = document.createElement('a');
                            a.href = url;
                            a.download = audioFile.name;
                            document.body.appendChild(a);
                            a.click();
                            document.body.removeChild(a);
                            setTimeout(() => URL.revokeObjectURL(url), 10000);
                          }}
                          className="text-emerald-400 hover:text-emerald-200 p-1"
                          title="Download audio file (save before refreshing)"
                        >
                          <Download className="w-3.5 h-3.5" />
                        </button>
                        <button
                          onClick={() => setAudioFile(null)}
                          className="text-purple-400 hover:text-purple-200"
                        >
                          <X className="w-3 h-3 md:w-4 md:h-4" />
                        </button>
                      </div>
                    </div>
                  )}
                  
                  {imageFiles.length > 0 && (
                    <div className="space-y-2">
                      <div className="flex items-center justify-between p-2 md:p-3 bg-emerald-900/40 rounded-lg border border-emerald-500/30">
                        <p className="text-xs md:text-sm text-emerald-200 flex items-center gap-2 font-semibold">
                          <Sparkles className="w-3 h-3 md:w-4 md:h-4 flex-shrink-0" />
                          <span>{imageFiles.length} images selected</span>
                        </p>
                        <button
                          onClick={() => setImageFiles([])}
                          className="text-emerald-400 hover:text-emerald-200 flex-shrink-0 ml-2"
                        >
                          <X className="w-3 h-3 md:w-4 md:h-4" />
                        </button>
                      </div>
                      <div className="max-h-32 overflow-y-auto space-y-1 p-2 bg-slate-900/40 rounded-lg">
                        {imageFiles.map((file, idx) => (
                          <div key={idx} className="text-xs text-slate-400 flex items-center gap-2">
                            <span className="text-emerald-400 font-mono">{idx + 1}.</span>
                            <span className="truncate">{file.name}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  <button
                    onClick={() => {
                      if (imageFiles.length > 0) {
                        // Batch image processing
                        handleProcessBatchImages(imageFiles);
                      } else if (audioFile) {
                        // Single file processing
                        // Check file type and route to appropriate handler
                        const isImage = isImageFile(audioFile);
                        const isDocument = audioFile.type === 'application/pdf' || 
                                          audioFile.type.includes('word') || 
                                          audioFile.name.endsWith('.pdf') || 
                                          audioFile.name.endsWith('.docx') || 
                                          audioFile.name.endsWith('.doc') || 
                                          audioFile.name.endsWith('.txt');
                        
                        if (isImage) {
                          handleProcessImage(audioFile);
                        } else if (isDocument) {
                          handleProcessDocument(audioFile);
                        } else {
                          handleProcessAudio();
                        }
                      }
                    }}
                    disabled={(!audioFile && imageFiles.length === 0) || isProcessing}
                    className={`w-full px-3 md:px-4 py-2.5 md:py-3 rounded-lg backdrop-blur-xl border-2 shadow-lg disabled:opacity-50 flex items-center justify-center gap-2 text-white text-sm md:text-base font-semibold ${
                      imageFiles.length > 0
                        ? 'bg-emerald-600/30 border-emerald-400/60 hover:bg-emerald-600/40'
                        : 'bg-purple-600/30 border-purple-400/60 hover:bg-purple-600/40'
                    }`}
                  >
                    {isProcessing ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin" />
                        {processingStep}
                      </>
                    ) : imageFiles.length > 0 ? (
                      <>
                        <Sparkles className="w-5 h-5" />
                        Process {imageFiles.length} Images
                      </>
                    ) : (
                      'Process File'
                    )}
                  </button>
                </div>

                {/* Tab Audio Recording */}
                <div className="rounded-xl border-2 border-amber-500/30 bg-amber-950/20 p-4 md:p-6 space-y-4">
                  <h3 className="text-base md:text-lg font-semibold text-amber-100 flex items-center gap-2">
                    <svg className="w-4 h-4 md:w-5 md:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
                    </svg>
                    Record System Audio
                  </h3>
                  
                  {isMobileDevice ? (
                    <div className="bg-orange-900/40 border-2 border-orange-500/50 rounded-lg p-4 space-y-2">
                      <div className="flex items-start gap-2">
                        <svg className="w-5 h-5 text-orange-300 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                        </svg>
                        <div>
                          <p className="text-sm font-semibold text-orange-100 mb-1">
                            Not Available on Mobile/Tablet
                          </p>
                          <p className="text-xs text-orange-200/80">
                            Tab audio recording requires desktop browser features not available on mobile devices (including iPad).
                          </p>
                          <p className="text-xs text-orange-200/80 mt-2">
                            <strong>Workaround:</strong> Use your device's built-in screen recorder, then upload the video file on desktop, or upload audio files directly above.
                          </p>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <>
                      <p className="text-xs md:text-sm text-amber-200/80">
                        Perfect for Canvas lectures! Capture audio directly from your browser tab.
                      </p>

                  {!isRecording ? (
                    <>
                      <div className="bg-amber-900/30 border border-amber-600/30 rounded-lg p-3 md:p-4 space-y-2">
                        <p className="text-xs md:text-sm text-amber-100 font-semibold">How it works:</p>
                        <ol className="text-[10px] md:text-xs text-amber-200/70 space-y-1 list-decimal list-inside">
                          <li>Click "Start Recording" below</li>
                          <li>Select the tab with your Canvas lecture</li>
                          <li>✅ Check "Share tab audio" in the browser prompt</li>
                          <li>Play your video</li>
                          <li>Click "Stop" when done</li>
                          <li>Auto-transcribes!</li>
                        </ol>
                      </div>

                      <button
                        onClick={startTabAudioRecording}
                        className="w-full px-3 md:px-4 py-3 md:py-4 rounded-lg bg-amber-600/30 backdrop-blur-xl border-2 border-amber-400/60 hover:bg-amber-600/40 text-white text-sm md:text-base font-bold shadow-lg transition-all flex items-center justify-center gap-2"
                      >
                        <svg className="w-5 h-5 md:w-6 md:h-6" fill="currentColor" viewBox="0 0 24 24">
                          <circle cx="12" cy="12" r="8" />
                        </svg>
                        Start Recording Tab Audio
                      </button>

                      <p className="text-[10px] md:text-xs text-amber-300/50 text-center">
                        Only captures audio from the selected tab (not your mic or other sounds)
                      </p>
                    </>
                  ) : (
                    <div className="space-y-4">
                      <div className="bg-red-900/30 border-2 border-red-500/50 rounded-lg p-4 md:p-6">
                        <div className="flex items-center justify-center gap-3 mb-4">
                          <div className="w-3 h-3 md:w-4 md:h-4 bg-red-500 rounded-full animate-pulse"></div>
                          <span className="text-base md:text-lg font-bold text-red-100">Recording...</span>
                        </div>
                        
                        <div className="text-center">
                          <p className="text-2xl md:text-4xl font-mono font-bold text-red-100 mb-2">
                            {formatDuration(recordingDuration) || '00:00'}
                          </p>
                          <p className="text-xs md:text-sm text-red-200/70">
                            Recording from browser tab • Session protected
                          </p>
                        </div>
                      </div>

                      <button
                        onClick={stopTabAudioRecording}
                        className="w-full px-3 md:px-4 py-3 md:py-4 rounded-lg bg-red-600/30 backdrop-blur-xl border-2 border-red-400/60 hover:bg-red-600/40 text-white text-sm md:text-base font-bold shadow-lg transition-all flex items-center justify-center gap-2"
                      >
                        <svg className="w-5 h-5 md:w-6 md:h-6" fill="currentColor" viewBox="0 0 24 24">
                          <rect x="6" y="6" width="12" height="12" rx="2" />
                        </svg>
                        Stop & Transcribe
                      </button>

                      <p className="text-xs text-amber-300/70 text-center">
                        You can minimize this window and continue playing your video
                      </p>
                    </div>
                  )}
                    </>
                  )}
                </div>

                {/* Text Input */}
                <div className="rounded-xl border-2 border-cyan-500/30 bg-cyan-950/20 p-4 md:p-6 space-y-4">
                  <h3 className="text-base md:text-lg font-semibold text-cyan-100">Or Paste Text/PDF Content</h3>
                  <textarea
                    className="w-full rounded-lg bg-slate-950/70 border border-cyan-500/50 text-slate-200 p-3 text-sm h-32 focus:outline-none focus:border-cyan-400 focus:ring-2 focus:ring-cyan-500/20"
                    placeholder="Paste your text here (PDF content, notes, transcript, etc.)"
                    value={contextText}
                    onChange={(e) => setContextText(e.target.value)}
                  />
                  <p className="text-xs text-cyan-300/60">
                    Tip: Copy text from PDF/Word documents and paste here
                  </p>
                  <button
                    onClick={handleUseContextText}
                    disabled={!contextText.trim()}
                    className="w-full px-3 md:px-4 py-2.5 md:py-3 rounded-lg bg-gradient-to-r from-cyan-600 to-blue-600 text-white text-sm md:text-base font-semibold shadow-lg disabled:opacity-50 disabled:cursor-not-allowed hover:brightness-110 transition-all"
                  >
                    {contextText.trim() ? 'Use This Text' : 'Paste text to continue'}
                  </button>
                </div>
              </div>
            </div>
          ) : transcript && !showQA ? (
            // Step 2: Show transcript and action buttons
            <div className="space-y-4">
              {/* ═══════════════ Success Popup ═══════════════ */}
              {showSuccessPopup && (
                <div className="rounded-xl border-2 border-emerald-500/30 bg-emerald-950/30 backdrop-blur-xl p-4 space-y-3">
                  <div className="flex items-center gap-2">
                    <svg className="w-5 h-5 text-emerald-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <div>
                      <p className="text-sm font-semibold text-emerald-200">✅ Transcription saved!</p>
                      <p className="text-xs text-emerald-300/70">Audio saved for playback. You can listen alongside the transcript.</p>
                    </div>
                    <button
                      onClick={() => setShowSuccessPopup(false)}
                      className="ml-auto p-1 hover:bg-emerald-800/40 rounded transition-colors"
                    >
                      <X className="w-4 h-4 text-emerald-400" />
                    </button>
                  </div>
                </div>
              )}
              <div className="rounded-xl border border-slate-700 bg-slate-900/50 p-4">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-semibold text-slate-300">Loaded Content:</h3>
                  <div className="flex items-center gap-3">
                    {/* Audio playback button */}
                    {audioUrl && (
                      <>
                        <audio ref={audioRef} onEnded={() => setIsPlaying(false)} className="hidden" />
                        <button
                          onClick={togglePlayback}
                          className={`flex items-center gap-1.5 px-3 py-1 rounded-lg text-xs font-medium transition-all ${
                            isPlaying ? 'bg-emerald-600 text-white' : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                          }`}
                          title={isPlaying ? 'Pause audio' : 'Play audio'}
                        >
                          {isPlaying ? (
                            <><Pause className="w-3 h-3" /> Playing</>
                          ) : (
                            <><Play className="w-3 h-3" /> Play Audio</>
                          )}
                        </button>
                      </>
                    )}
                    {transcriptSegments.length > 0 && (
                      <button
                        onClick={() => setShowTimestamps(!showTimestamps)}
                        className={`px-3 py-1 rounded-lg text-xs font-medium transition-all ${
                          showTimestamps 
                            ? 'bg-cyan-600 text-white' 
                            : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                        }`}
                      >
                        {showTimestamps ? '🕐 Timestamps ON' : '🕐 Timestamps OFF'}
                      </button>
                    )}
                    <span className="text-xs text-slate-500">
                      {documentExtracted ? `Document: ${documentExtracted.fileName}` : audioFile?.name ? `Audio: ${audioFile.name}` : 'Pasted Text'}
                    </span>
                  </div>
                </div>
                <div className="max-h-48 overflow-y-auto">
                  <pre className="text-sm text-slate-400 whitespace-pre-wrap font-sans">
                    {getFormattedTranscript()}
                  </pre>
                </div>
              </div>

              {/* Document Info */}
              {documentExtracted && (
                <div className="rounded-lg bg-purple-900/20 border border-purple-500/30 p-3">
                  <div className="flex items-center justify-between text-xs text-purple-200">
                    <div className="flex items-center gap-4">
                      <span>📄 {documentExtracted.type.toUpperCase()}</span>
                      {documentExtracted.pageCount && <span>📖 {documentExtracted.pageCount} pages</span>}
                      <span>📝 {documentExtracted.wordCount.toLocaleString()} words</span>
                    </div>
                  </div>
                </div>
              )}

              {/* Generate Summary Button */}
              {!summary && (
                <button
                  onClick={handleGenerateSummary}
                  disabled={summaryLoading}
                  className="w-full px-6 py-4 rounded-xl bg-gradient-to-r from-purple-600 to-pink-600 text-white text-lg font-bold shadow-2xl shadow-purple-500/40 hover:shadow-purple-500/60 hover:scale-[1.02] transition-all flex items-center justify-center gap-3 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {summaryLoading ? (
                    <>
                      <Loader2 className="w-6 h-6 animate-spin" />
                      Generating Summary...
                    </>
                  ) : (
                    <>
                      <Sparkles className="w-6 h-6" />
                      ✨ Generate AI Summary
                    </>
                  )}
                </button>
              )}

              {/* Summary Display */}
              {summary && (
                <div className="rounded-xl border-2 border-amber-500/30 bg-gradient-to-br from-amber-950/40 via-orange-950/30 to-amber-900/40 p-6 space-y-4">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-bold text-amber-100 flex items-center gap-2">
                      <Sparkles className="w-5 h-5 text-amber-400" />
                      AI Summary
                    </h3>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => {
                          navigator.clipboard.writeText(summary);
                          alert('Summary copied to clipboard!');
                        }}
                        className="p-2 rounded-lg bg-amber-600/20 hover:bg-amber-600/30 text-amber-300 transition-all"
                        title="Copy summary"
                      >
                        <Copy className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => setShowSummary(!showSummary)}
                        className="p-2 rounded-lg bg-amber-600/20 hover:bg-amber-600/30 text-amber-300 transition-all"
                      >
                        {showSummary ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                  
                  {showSummary && (
                    <div className="prose prose-invert prose-amber max-w-none text-sm">
                      <div className="whitespace-pre-wrap text-amber-50 leading-relaxed">
                        {summary}
                      </div>
                    </div>
                  )}
                </div>
              )}

              <button
                onClick={() => setShowQA(true)}
                className="w-full px-6 py-4 rounded-xl bg-gradient-to-r from-amber-600 via-orange-600 to-amber-700 text-white text-lg font-bold shadow-2xl shadow-amber-500/40 hover:shadow-amber-500/60 hover:scale-[1.02] transition-all flex items-center justify-center gap-3"
              >
                <Sparkles className="w-6 h-6 animate-pulse" />
                Start Free Q&A ✨
              </button>
            </div>
          ) : (
            // Step 3: Q&A Interface
            <TranscriptQA
              transcript={transcript}
              transcriptSegments={transcriptSegments}
              showTimestamps={showTimestamps}
              qaPairs={qaPairs}
              currentQuestion={currentQuestion}
              questionTemplate={questionTemplate}
              showTemplateEdit={showTemplateEdit}
              isGenerating={isGenerating}
              editingIndex={editingIndex}
              editedQuestion={editedQuestion}
              searchQuery={searchQuery}
              onSetCurrentQuestion={setCurrentQuestion}
              onSetQuestionTemplate={setQuestionTemplate}
              onSetShowTemplateEdit={setShowTemplateEdit}
              onSetEditingIndex={setEditingIndex}
              onSetEditedQuestion={setEditedQuestion}
              onSetSearchQuery={setSearchQuery}
              onSetShowTimestamps={setShowTimestamps}
              onGetAnswer={handleGetAnswer}
              onRegenerateAnswer={handleRegenerateAnswer}
              onCopyAll={handleCopyAll}
              onDownload={handleDownload}
              getFormattedTranscript={getFormattedTranscript}
            />
          )}
        </div>
        </div>
      </div>
        );
        
        // When minimized, don't render the full overlay at all
        if (isMinimized) return panelContent;
        
        return embedded ? panelContent : (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-0 md:p-4 bg-black/60 backdrop-blur-sm">
            {panelContent}
          </div>
        );
      })()}
    </>
  );
};

export default WhiptTranscriptsPanel;
